import { ReactNode, useCallback, useState } from 'react';
import type { TabNavigationSpec } from './_tab_navigation.context';
import { TabNavigationContext } from './_tab_navigation.context';

interface Props {
	children: ReactNode;
	defaultValue?: string;
	onChange?: (value: string) => void;
}

/* @internal */
export function TabNavigationProvider({ children, onChange, defaultValue }: Props): JSX.Element {
	const [tabs, setTabs] = useState<TabNavigationSpec[]>([]);
	const [activeTab, setActiveTab] = useState<string | undefined>(defaultValue);

	const handleChangeTab = useCallback(
		(tab: string) => {
			setActiveTab(tab);

			if (onChange != null) {
				onChange(tab);
			}
		},
		[onChange],
	);

	const handleRegisterTab = useCallback((tab: TabNavigationSpec) => {
		setTabs((prev) => {
			const index = prev.findIndex((prevTab) => prevTab.value === tab.value);
			if (index === -1) {
				return [...prev, tab];
			}

			return [...prev.slice(0, index), tab, ...prev.slice(index + 1)];
		});
	}, []);

	const handleUnregisterTab = useCallback((tab: string) => {
		setTabs((prev) => {
			const index = prev.findIndex((prevTab) => prevTab.value === tab);
			if (index === -1) {
				return prev;
			}
			const tabItem = prev[index];

			return [
				...prev.slice(0, index),
				{
					...tabItem,
					removed: true,
				},
				...prev.slice(index + 1),
			];
		});
	}, []);

	return (
		<TabNavigationContext.Provider
			value={{
				tabs: tabs,
				activeTab: activeTab,
				changeTab: handleChangeTab,
				registerTab: handleRegisterTab,
				unregisterTab: handleUnregisterTab,
			}}
		>
			{children}
		</TabNavigationContext.Provider>
	);
}
